import { get } from 'lodash'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { useLazyGetRouteStopsGeojsonQuery } from 'api/route/getRouteStops'
import { readDriverPathSegments } from 'components/common/map/readDriverPathSegments'
import { toRequestableRouteIds } from 'components/common/map/requestableRouteIds'
import { createDateFromString, formatDateToBEFormatDateFns, isNotInvalidDate } from 'utils/date'

// Driver-path breadcrumbs for a set of routes on an ARBITRARY date — the bulk maps
// overlay a reference path independent of the dialog's service date (yesterday's, say).
// Resolves each route's routeDailyGeoJsonId from that date's geojson, then reads its
// firestore breadcrumb. Keyed by route, not merged, so each draws in its own colour.
export const useRoutesDriverPathBreadcrumb = ({ routeIds = [], date, enabled = true }) => {
  const businessId = useSelector(state => get(state, 'AuthReducer.userInfo.businessInfo.businessId'))
  const [breadcrumbsByRouteId, setBreadcrumbsByRouteId] = useState(null)
  const [getGeojson] = useLazyGetRouteStopsGeojsonQuery()

  const idsKey = toRequestableRouteIds(routeIds).join(',')
  // Coerce + guard invalid dates — a partial/cleared date picker yields an
  // invalid Date, and date-fns `format` throws on it (white-screen crash).
  const dateObj = typeof date === 'string' ? createDateFromString(date) : date
  const beDate = isNotInvalidDate(dateObj) ? formatDateToBEFormatDateFns(dateObj) : null

  useEffect(() => {
    const ids = idsKey ? idsKey.split(',') : []
    if (!enabled || ids.length === 0 || !beDate || !businessId) {
      setBreadcrumbsByRouteId(null)
      return undefined
    }
    let cancelled = false
    const run = async () => {
      try {
        const data = await getGeojson({ routes: ids, serviceDate: beDate }).unwrap()
        const routeGeojsonData = get(data, 'routeGeojsonData', {})
        const entries = await Promise.all(
          ids.map(async id => {
            const dailyId = get(routeGeojsonData, `${id}.routeDailyGeoJsonId`)
            if (!dailyId) return null
            const segments = await readDriverPathSegments(businessId, dailyId)
            return segments?.length > 0 ? [id, segments] : null
          })
        )
        if (cancelled) return
        const byRouteId = Object.fromEntries(entries.filter(Boolean))
        setBreadcrumbsByRouteId(Object.keys(byRouteId).length > 0 ? byRouteId : null)
      } catch {
        if (!cancelled) setBreadcrumbsByRouteId(null)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [enabled, idsKey, beDate, businessId, getGeojson])

  return breadcrumbsByRouteId
}

export default useRoutesDriverPathBreadcrumb
